import { useEffect, useRef, useState } from 'react';
import { StreetViewTokenService } from '@/services/streetViewToken';

const PANORAMA_SEARCH_RADIUS_METERS = 50;
const SCRIPT_ID = 'google-maps-js-api';

type Props = {
	latitude: number;
	longitude: number;
};

const READY_CALLBACK = '__googleMapsReady';

declare global {
	interface Window {
		google?: any;
		__googleMapsLoader?: Promise<void>;
		__googleMapsReady?: () => void;
	}
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
	if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
	if (window.google?.maps?.StreetViewPanorama) return Promise.resolve();
	if (window.__googleMapsLoader) return window.__googleMapsLoader;

	window.__googleMapsLoader = new Promise<void>((resolve, reject) => {
		const fail = () => reject(new Error('Failed to load Google Maps'));
		window.__googleMapsReady = () => resolve();

		const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
		if (existing) {
			if (window.google?.maps?.StreetViewPanorama) resolve();
			existing.addEventListener('error', fail);
			return;
		}
		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&loading=async&callback=${READY_CALLBACK}`;
		script.async = true;
		script.defer = true;
		script.onerror = fail;
		document.head.appendChild(script);
	});

	return window.__googleMapsLoader;
}

function findNearestPanorama(
	google: any,
	latitude: number,
	longitude: number,
): Promise<string | null> {
	return new Promise((resolve) => {
		const service = new google.maps.StreetViewService();
		service.getPanorama(
			{
				location: { lat: latitude, lng: longitude },
				radius: PANORAMA_SEARCH_RADIUS_METERS,
				source: google.maps.StreetViewSource.OUTDOOR,
			},
			(data: any, status: any) => {
				if (status === google.maps.StreetViewStatus.OK && data?.location?.pano) {
					resolve(data.location.pano);
				} else {
					resolve(null);
				}
			},
		);
	});
}

export default function StreetView({ latitude, longitude }: Props) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const panoramaRef = useRef<any>(null);
	const markerRef = useRef<any>(null);
	const [status, setStatus] = useState<'idle' | 'loading' | 'no-image' | 'no-token' | 'error'>(
		'idle',
	);

	useEffect(() => {
		if (!containerRef.current) return;

		let cancelled = false;
		setStatus('loading');

		(async () => {
			try {
				const apiKey =
					(await StreetViewTokenService.getValidToken()) ||
					import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
					null;
				if (cancelled) return;
				if (!apiKey) {
					setStatus('no-token');
					return;
				}

				await loadGoogleMapsScript(apiKey);
				if (cancelled || !containerRef.current) return;

				const google = window.google;
				const panoId = await findNearestPanorama(google, latitude, longitude);
				if (cancelled) return;
				if (!panoId) {
					setStatus('no-image');
					return;
				}

				const targetPosition = { lat: latitude, lng: longitude };

				if (!panoramaRef.current) {
					panoramaRef.current = new google.maps.StreetViewPanorama(containerRef.current, {
						pano: panoId,
						pov: { heading: 0, pitch: 0 },
						zoom: 1,
						addressControl: false,
						fullscreenControl: false,
						motionTrackingControl: false,
						showRoadLabels: false,
					});
				} else {
					panoramaRef.current.setPano(panoId);
				}

				if (markerRef.current) {
					markerRef.current.setMap(null);
				}
				markerRef.current = new google.maps.Marker({
					position: targetPosition,
					map: panoramaRef.current,
					icon: {
						path: google.maps.SymbolPath.CIRCLE,
						scale: 8,
						fillColor: '#E23B3B',
						fillOpacity: 1,
						strokeColor: '#FFFFFF',
						strokeWeight: 2,
					},
				});

				setStatus('idle');
			} catch (error) {
				if (!cancelled) setStatus('error');
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [latitude, longitude]);

	useEffect(() => {
		return () => {
			if (markerRef.current) {
				markerRef.current.setMap(null);
				markerRef.current = null;
			}
			panoramaRef.current = null;
		};
	}, []);

	return (
		<>
			<div ref={containerRef} className="adjust-coordinates-viewport__street" />
			{status === 'no-token' && (
				<div className="adjust-coordinates-viewport__placeholder">
					Street View is not available right now.
				</div>
			)}
			{status === 'no-image' && (
				<div className="adjust-coordinates-viewport__placeholder">
					No Street View imagery available near this location.
				</div>
			)}
			{status === 'error' && (
				<div className="adjust-coordinates-viewport__placeholder">
					Unable to load Street View imagery.
				</div>
			)}
		</>
	);
}
